import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";

// Map from route pattern → section key used by the backend
const ROUTE_TO_SECTION = [
  { test: (p) => p === "/login", key: "login" },
  { test: (p) => p === "/register", key: "register" },
  { test: (p) => p === "/perfil", key: "perfil" },
  { test: (p) => p.startsWith("/pronosticos/fase1"), key: "fase1" },
  { test: (p) => p.startsWith("/pronosticos/fase2"), key: "fase2" },
  { test: (p) => p === "/ranking", key: "ranking" },
  { test: (p) => p === "/reglas", key: "reglas" },
  { test: (p) => p === "/todos", key: "todos" },
  // Noticias has two flavors: authenticated vs public. The component will pass `forcePublic`.
  { test: (p) => p.startsWith("/noticias"), key: "noticias" },
  { test: (p) => p.startsWith("/admin"), key: "admin" },
];

export function resolveSectionKey(pathname, { forcePublic = false } = {}) {
  for (const { test, key } of ROUTE_TO_SECTION) {
    if (test(pathname)) {
      if (forcePublic && key === "noticias") return "noticias_public";
      return key;
    }
  }
  return null;
}

export function absoluteImageUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const base = API_BASE.replace(/\/api$/, "");
  return `${base}${url}`;
}

let _cache = null;
let _inflight = null;
const _subscribers = new Set();

async function fetchBackgrounds() {
  if (_inflight) return _inflight;
  _inflight = api
    .get("/section-backgrounds")
    .then((r) => {
      _cache = r.data || {};
      _subscribers.forEach((cb) => cb(_cache));
      return _cache;
    })
    .catch(() => {
      _cache = {};
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

// Public method to invalidate cache after admin saves
export function invalidateBackgroundsCache() {
  _cache = null;
  fetchBackgrounds();
}

/**
 * Returns the resolved absolute background image URL for the current route,
 * or null if no background is configured. Auto-refetches on mount.
 */
export function useSectionBackground({ forcePublic = false } = {}) {
  const location = useLocation();
  const sectionKey = resolveSectionKey(location.pathname, { forcePublic });
  const [map, setMap] = useState(_cache);

  useEffect(() => {
    const onChange = (m) => setMap({ ...m });
    _subscribers.add(onChange);
    if (!_cache) fetchBackgrounds();
    return () => {
      _subscribers.delete(onChange);
    };
  }, []);

  const raw = sectionKey && map ? map[sectionKey] : null;
  return { sectionKey, imageUrl: absoluteImageUrl(raw) };
}

/** Get a specific named background image URL (e.g., "login_hero"). */
export function useNamedBackground(key) {
  const [map, setMap] = useState(_cache);
  useEffect(() => {
    const onChange = (m) => setMap({ ...m });
    _subscribers.add(onChange);
    if (!_cache) fetchBackgrounds();
    return () => { _subscribers.delete(onChange); };
  }, []);
  const raw = map ? map[key] : null;
  return absoluteImageUrl(raw);
}
